import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import defaults from 'lodash/defaults';
import pick from 'lodash/pick';

import { Checkbox } from '@pwc/appkit-react/lib/Checkbox';
import Button from '../../../shared/button.component';

import FormHelpers from '../../../shared/forms/formHelpers';
import FormControl from '../../../shared/forms/formControl.component';
import Input from '../../../shared/forms/input/input.component';
import TextArea from '../../../shared/forms/textarea/textarea.component';
import { defaultServerSideParams, CustomReportParamTypes } from '../../../shared/customReports/constants';
import { getParamNamesForQuery, updateParamsFormData, updateQueryParamValues } from '../../../shared/customReports/helpers';

import getInitialState from './customReportEditor.initialState';
import EditCustomReportQueryModal from './editCustomReportQueryModal.component';
import TestCustomReportQueryForm from './testCustomReportQueryForm.component';
import CustomReportModal from '../customReportModal.component';
import ControlButtons from '../controlButtons.component';
import ValidationConfirmationModal from '../validationConfirmationModal.component';

import { customReportQueryPropTypes, customReportDataPropTypes } from '../../../shared/customReports/propTypes';
import { OptionsPropType } from '../../../shared/propTypes/common';

class CustomReportEditor extends PureComponent {
  static propTypes = {
    saveCustomReport: PropTypes.func.isRequired,
    setCustomReportQueryModified: PropTypes.func.isRequired,
    showCustomReportQueryForm: PropTypes.func.isRequired,
    hideCustomReportForm: PropTypes.func.isRequired,
    showCustomReportModalForEdit: PropTypes.func.isRequired,
    disableReadOnlyMode: PropTypes.func.isRequired,
    testCustomReportQuery: PropTypes.func.isRequired,
    hideCustomReportModalForEdit: PropTypes.func.isRequired,
    fetchCustomReportParams: PropTypes.func.isRequired,
    showCustomReportModalForParam: PropTypes.func.isRequired,
    hideCustomReportModalForParam: PropTypes.func.isRequired,
    closeCustomReportPreview: PropTypes.func.isRequired,

    customReportData: customReportDataPropTypes,
    customReportQueryToEdit: customReportQueryPropTypes.isRequired,

    canModify: PropTypes.bool.isRequired,
    isSavingCustomReportQueryInProgress: PropTypes.bool.isRequired,
    isTestCustomReportQueryInProgress: PropTypes.bool.isRequired,
    isFetchingCustomReportParams: PropTypes.bool.isRequired,
    readOnly: PropTypes.bool.isRequired,
    openForEdit: PropTypes.bool.isRequired,
    wasVerifyingCustomReportParamQuerySuccessful: PropTypes.bool.isRequired,

    customReportParamsOptions: PropTypes.objectOf(OptionsPropType),

    reportParamToEditOptions: OptionsPropType,

    editCustomReportModalData: PropTypes.shape({
      show: PropTypes.bool.isRequired,
      data: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        sql: PropTypes.string.isRequired,
      }),
    }),

    customReportParamModalData: PropTypes.shape({
      show: PropTypes.bool.isRequired,
      data: PropTypes.objectOf(PropTypes.string),
    }).isRequired,

    testCustomReportQueryParamModalData: PropTypes.func.isRequired,
    showTestCustomReportQueryParamModal: PropTypes.func.isRequired,
    hideTestCustomReportQueryParamModal: PropTypes.func.isRequired,
    verifyParamOptionsQuery: PropTypes.func.isRequired,
    showExportCustomReportInsertQueryModal: PropTypes.func.isRequired,

    isVerifyingParamToEditQuery: PropTypes.bool.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      ...getInitialState(props.customReportQueryToEdit),
      showValidationModal: false,
      validationMessage: '',
      pendingExecution: null,
    };
  }

  validate = (key, { isValid }) => {
    FormHelpers.validate({
      key,
      isValid,
      setState: this.setState.bind(this),
      callback: () => {
        this.setState({
          shouldResetValues: false,
        }, () => {
          this.validateAll();
        });
      },
    });
  };

  validationSubscription = (key, operation) => {
    FormHelpers.validationSubscription({
      key,
      operation,
      setState: this.setState.bind(this),
      callback: this.validateAll,
    });
  };

  validateAll = () => FormHelpers.validateAll(this.setState.bind(this));

  saveCustomReportQuery = () => {
    const {
      saveCustomReport,
      setCustomReportQueryModified,
      showCustomReportQueryForm,
      openForEdit,
    } = this.props;

    const saveStrategy = data => saveCustomReport({
      data,
      isEditMode: openForEdit,
    }, () => {
      showCustomReportQueryForm(data.id);
      if (setCustomReportQueryModified) {
        setCustomReportQueryModified(false);
      }
    });

    this.save(saveStrategy);
  };

  save = saveStrategy => {
    const { formData, isFormValid, paramsFormData, queryParams } = this.state;

    if (!isFormValid) {
      this.setState({
        triedToSubmitForm: true,
      });

      return;
    }

    const paramNames = getParamNamesForQuery(formData.query.value);

    const data = {
      id: formData.id.value,
      customFlag: formData.display.value,
      name: formData.name.value,
      sql: formData.query.value,
      serverSide: formData.serverSide.value,
      defaultOrderBy: formData.defaultOrderBy.value,
      customReportType: formData.customReportType.value,
      params: paramsFormData,
      queryParams: {
        ...(formData.serverSide.value ? defaultServerSideParams : {}),
        ...pick(queryParams, paramNames),
      },
    };

    saveStrategy(data);
  };

  updateQueryParams = queryParams =>
    this.setState(state => ({
      queryParams: {
        ...state.queryParams,
        ...queryParams,
      },
    }));

  updateParam = param => {
    this.setState(({ paramsFormData, queryParams, formData }) => {
      const newParamsFormData = updateParamsFormData(paramsFormData, formData.query.value, param);
      const newQueryParams = updateQueryParamValues(queryParams, newParamsFormData);

      return {
        paramsFormData: newParamsFormData,
        queryParams: newQueryParams,
      };
    });
  };

  runValidators = () => {
    const { paramsFormData, queryParams } = this.state;

    const validatorParams = Object.values(paramsFormData).filter(param =>
      param.type === CustomReportParamTypes.validator
    );

    if (validatorParams.length === 0) {
      this.executeReportDirectly();
      return;
    }

    let validatorsToCheck = validatorParams.length;
    let hasValidationError = false;
    let validationMessage = '';

    const onValidationSuccess = response => {
      validatorsToCheck--;

      if (response && response.length > 0) {
        const validationResult = response[0].validationResult
          || response[0].validation_result || response[0].VALIDATION_RESULT;
        if (validationResult && validationResult !== 'OK') {
          hasValidationError = true;
          validationMessage = validationResult;
        }
      }

      if (validatorsToCheck === 0) {
        if (hasValidationError) {
          this.setState({
            showValidationModal: true,
            validationMessage,
            pendingExecution: this.executeReportDirectly,
          });
        } else {
          this.executeReportDirectly();
        }
      }
    };

    const onValidationFailure = () => {
      validatorsToCheck--;

      if (validatorsToCheck === 0) {
        if (!hasValidationError) {
          this.executeReportDirectly();
        }
      }
    };

    validatorParams.forEach(validatorParam => {
      const paramNames = getParamNamesForQuery(validatorParam.query);
      const validationParams = pick(queryParams, paramNames);

      this.props.verifyParamOptionsQuery({
        query: validatorParam.query,
        params: validationParams,
        onSuccess: onValidationSuccess,
        onFailure: onValidationFailure,
      });
    });
  };

  handleValidationCancel = () => {
    this.setState({
      showValidationModal: false,
      validationMessage: '',
      pendingExecution: null,
    });
  };

  handleValidationProceed = () => {
    const { pendingExecution } = this.state;
    this.setState({
      showValidationModal: false,
      validationMessage: '',
      pendingExecution: null,
    }, () => {
      if (pendingExecution) {
        pendingExecution();
      }
    });
  };

  handleAddValidator = () => {
    this.props.showCustomReportModalForParam();
  };

  handleEditValidator = validator => {
    this.props.showCustomReportModalForParam(validator);
  };

  handleRemoveValidator = validatorName => {
    const { paramsFormData } = this.state;
    const updatedParams = { ...paramsFormData };
    delete updatedParams[validatorName];

    this.setState({
      paramsFormData: updatedParams,
    });
  };

  createEditValidatorHandler = validator => () => {
    this.handleEditValidator(validator);
  };

  createRemoveValidatorHandler = validatorName => () => {
    this.handleRemoveValidator(validatorName);
  };

  handleServerSideChange = e => {
    this.changeProperty({ target: { name: 'serverSide', value: e.target.checked } });
  };

  getValidators = () => {
    const { paramsFormData } = this.state;
    return Object.values(paramsFormData).filter(param =>
      param.type === CustomReportParamTypes.validator
    );
  };

  executeReportDirectly = () => {
    const { formData, queryParams } = this.state;
    const paramNames = getParamNamesForQuery(formData.query.value);

    this.props.testCustomReportQuery({
      query: formData.query.value,
      customReportType: formData.customReportType.value,
      name: formData.name.value,
      params: {
        ...(formData.serverSide.value ? defaultServerSideParams : {}),
        ...pick(queryParams, paramNames),
      },
    });
  };

  testCustomReportQuery = () => {
    this.runValidators();
  };

  getQueryParams = query => {
    const { paramsFormData, queryParams } = this.state;
    const { customReportQueryToEdit } = this.props;

    const newParamsFormData = updateParamsFormData(
      defaults(customReportQueryToEdit ? { ...customReportQueryToEdit.params } : {}, paramsFormData),
      query
    );
    const newQueryParams = updateQueryParamValues(queryParams, newParamsFormData);

    return {
      paramsFormData: newParamsFormData,
      queryParams: newQueryParams,
    };
  };

  changeQuery = event => {
    this.changeProperty(event);
    const params = this.getQueryParams(event.target.value);
    this.setState(params);
  };

  changeProperty = event => {
    FormHelpers.changeProperty({
      event,
      setState: this.setState.bind(this),
      callback: () => {
        this.props.setCustomReportQueryModified(true);
      },
    });
  };

  validateQueryName = value =>
    !(new RegExp('^[a-zA-Z0-9 _-]*$').test(value)) && 'Query Name can only contain alphanumeric characters, "-", "_" and spaces.';

  showCustomReportModalForEdit = () => {
    const { customReportQueryToEdit, showCustomReportModalForEdit } = this.props;
    const { formData } = this.state;

    showCustomReportModalForEdit({
      id: customReportQueryToEdit.reportSetId,
      name: formData.name.value,
      sql: formData.query.value,
    });
  };

  showInsertQueryModal = () => {
    const { showExportCustomReportInsertQueryModal, customReportQueryToEdit } = this.props;
    showExportCustomReportInsertQueryModal({
      reportSetId: customReportQueryToEdit.reportSetId,
    });
  };

  render() {
    const {
      formData,
      triedToSubmitForm,
      shouldResetValues,
      paramsFormData,
      queryParams,
    } = this.state;

    const {
      customReportData,
      closeCustomReportPreview,
      readOnly,
      canModify,
      openForEdit,
      customReportQueryToEdit,
      isSavingCustomReportQueryInProgress,
      isTestCustomReportQueryInProgress,
      isFetchingCustomReportParams,
      editCustomReportModalData,
      hideCustomReportModalForEdit,
      fetchCustomReportParams,
      customReportParamsOptions,
      showCustomReportModalForParam,
      customReportParamModalData,
      hideCustomReportModalForParam,
      reportParamToEditOptions,
      disableReadOnlyMode,
      hideCustomReportForm,

      testCustomReportQueryParamModalData,
      showTestCustomReportQueryParamModal,
      hideTestCustomReportQueryParamModal,
      isVerifyingParamToEditQuery,
      verifyParamOptionsQuery,
    } = this.props;

    const isLoadingDataToEdit = !!openForEdit && !customReportQueryToEdit;

    const areValuesValid = formData.name.isValid && formData.query.isValid;

    const isReportModified = !!openForEdit
      && (
        formData.name.value !== customReportQueryToEdit.reportSetName
        || formData.query.value !== customReportQueryToEdit.sql
      );

    return (
      <div className="custom-report-editor-form page-content-container">
        <form>
          <div className="row">
            <div className="col-sm-12 col-lg-6">
              <FormControl
                onChange={this.changeProperty}
                name="name"
                label="Query name"
                validate={this.validate}
                validationSubscription={this.validationSubscription}
                value={formData.name.value}
                required
                showErrorByForce={triedToSubmitForm}
                shouldReset={shouldResetValues}
                horizontal
                customValidation={this.validateQueryName}
              >
                <Input
                  disabled={isLoadingDataToEdit || readOnly}
                  type="text"
                />
              </FormControl>
              <FormControl
                onChange={this.changeQuery}
                name="query"
                label="Query"
                validate={this.validate}
                validationSubscription={this.validationSubscription}
                value={formData.query.value}
                required
                showErrorByForce={triedToSubmitForm}
                shouldReset={shouldResetValues}
                horizontal
              >
                <TextArea
                  resize="vertical"
                  className="custom-report-editor-form__textarea"
                  disabled={isLoadingDataToEdit || readOnly}
                />
              </FormControl>
              <div className="form-group">
                <div className="row">
                  <div className="col-sm-3">
                    <label className="form-control-label">
                      Validators
                    </label>
                  </div>
                  <div className="col-sm-9">
                    {!readOnly && (
                      <Button
                        type="button"
                        kind="primary"
                        className="mb-3"
                        onClick={this.handleAddValidator}
                        disabled={isLoadingDataToEdit}
                        title="Add a new validator parameter"
                      >
                        <i className="fas fa-plus mr-1" />
                        Add Validator
                      </Button>
                    )}

                    {this.getValidators().length > 0 ? (
                      <div className="validators-list">
                        <small className="d-block text-muted mb-2">
                          <strong>{this.getValidators().length}</strong> validator{this.getValidators().length !== 1 ? 's' : ''} configured:
                        </small>
                        {this.getValidators().map(validator => (
                          <div key={validator.name} className="validator-item bg-light p-3 mb-2 rounded border">
                            <div className="d-flex justify-content-between align-items-start">
                              <div className="flex-grow-1 mr-3">
                                <div className="d-flex align-items-center mb-1">
                                  <i className="fas fa-shield-alt text-primary mr-2" />
                                  <strong className="text-dark">{validator.mappingName}</strong>
                                </div>
                                <small className="d-block text-muted">
                                  {validator.errorTemplate || validator.query}
                                </small>
                              </div>
                              {!readOnly && (
                                <div className="d-flex">
                                  <Button
                                    type="button"
                                    kind="primary"
                                    className="mr-2"
                                    onClick={this.createEditValidatorHandler(validator)}
                                    title={`Edit validator: ${validator.mappingName}`}
                                  >
                                    <i className="fas fa-edit mr-1" />
                                    Edit
                                  </Button>
                                  <Button
                                    type="button"
                                    kind="primary"
                                    negative
                                    onClick={this.createRemoveValidatorHandler(validator.name)}
                                    title={`Remove validator: ${validator.mappingName}`}
                                  >
                                    <i className="fas fa-times mr-1" />
                                    Remove
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="no-validators-message text-muted">
                        <i className="fas fa-shield-alt mr-1" />
                        No validators configured. Add one to validate user input.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-group">
                <div className="row">
                  <div className="col-sm-3" />
                  <div className="col-sm-9">
                    <div className="custom-control custom-checkbox">
                      <Checkbox
                        disabled={isLoadingDataToEdit || readOnly}
                        checked={formData.serverSide.value}
                        onChange={this.handleServerSideChange}
                      >
                        Server Side Data
                      </Checkbox>
                    </div>
                  </div>
                </div>
              </div>
              {formData.serverSide.value && (
                <FormControl
                  onChange={this.changeProperty}
                  name="defaultOrderBy"
                  label="Default Order By"
                  validate={this.validate}
                  validationSubscription={this.validationSubscription}
                  value={formData.defaultOrderBy.value}
                  showErrorByForce={triedToSubmitForm}
                  shouldReset={shouldResetValues}
                  horizontal
                >
                  <Input
                    disabled={isLoadingDataToEdit || readOnly}
                    type="text"
                  />
                </FormControl>)
              }
            </div>
            <TestCustomReportQueryForm
              params={paramsFormData}
              queryParams={queryParams}
              isFetchingCustomReportParams={isFetchingCustomReportParams}
              updateQueryParams={this.updateQueryParams}
              fetchCustomReportParams={fetchCustomReportParams}
              readOnly={readOnly}
              customReportParamsOptions={customReportParamsOptions}
              showCustomReportModalForParam={showCustomReportModalForParam}
              hideCustomReportModalForParam={hideCustomReportModalForParam}
              customReportParamModalData={customReportParamModalData}
              testCustomReportQueryParamModalData={testCustomReportQueryParamModalData}
              showTestCustomReportQueryParamModal={showTestCustomReportQueryParamModal}
              hideTestCustomReportQueryParamModal={hideTestCustomReportQueryParamModal}
              updateParam={this.updateParam}
              reportParamToEditOptions={reportParamToEditOptions}
              isVerifyingParamToEditQuery={isVerifyingParamToEditQuery}
              verifyParamOptionsQuery={verifyParamOptionsQuery}
            />
          </div>
        </form>
        <ControlButtons
          readOnly={readOnly}
          canModify={canModify}
          isTestInProgress={isTestCustomReportQueryInProgress}
          isSavingInProgress={isSavingCustomReportQueryInProgress}
          isLoadingDataToEdit={isLoadingDataToEdit}
          areValuesValid={areValuesValid}
          onTestClick={this.testCustomReportQuery}
          onCancelClick={hideCustomReportForm}
          onEditClick={disableReadOnlyMode}
          onSaveClick={isReportModified
            ? this.showCustomReportModalForEdit
            : this.saveCustomReportQuery}
          onShowInsertQueryModal={this.showInsertQueryModal}
        />

        <CustomReportModal
          customReportData={customReportData}
          formData={formData}
          queryParams={queryParams}
          hideModal={closeCustomReportPreview}
          isVisible={Boolean(customReportData)}
        />

        <EditCustomReportQueryModal
          hideModal={hideCustomReportModalForEdit}
          saveEntity={this.saveCustomReportQuery}
          entityToEdit={editCustomReportModalData.data}
          isVisible={editCustomReportModalData.show}
        />

        <ValidationConfirmationModal
          isVisible={this.state.showValidationModal}
          validationMessage={this.state.validationMessage}
          onCancel={this.handleValidationCancel}
          onProceed={this.handleValidationProceed}
        />

      </div>
    );
  }
}

export default CustomReportEditor;
